import { Button, Flex, Modal, Pagination, Paper } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus, IconRefresh } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import {
  useGetCrossCameraAppList,
  useGetCrossCameraModels,
  useGetCrossCameraPipelineList
} from '@/queries/crossCameraQueries'
import { CrossCameraPipeline } from '@/types/crossCamera'
import { CrudAction, Resource } from '@/types/permissions'
import {
  MAX_ITEMS_WITHOUT_PAGINATION,
  getPageCount,
  getPageOffset
} from '@/utils/pagination'
import { useTableState } from '../ui-shared/Tables/useTableState'
import { CrossCameraPipelineDeleteModal } from './CrossCameraPipelineDeleteModal/CrossCameraPipelineDeleteModal'
import { CrossCameraPipelineList } from './CrossCameraPipelineList/CrossCameraPipelineList'
import { CrossCameraPipelineRestartModal } from './CrossCameraPipelineRestartModal/CrossCameraPipelineRestartModal'
import { CrossCameraPipelineFormHandler } from './pipeline-create/CrossCameraPipelineFormHandler'

const PAGE_SIZE = 10
const POLL_INTERVAL = 20000

export const CrossCameraPipelinesPage = () => {
  const intl = useIntl()
  const [tableState, setTableState] = useTableState({ activePage: 1 })
  const { activePage } = tableState
  const [canCreate] = useHasPermissions(Resource.Applications, [
    CrudAction.Update
  ])

  const [formOpen, { open: openForm, close: closeForm }] = useDisclosure(false)
  const [editing, setEditing] = useState<CrossCameraPipeline | null>(null)
  const [toDelete, setToDelete] = useState<CrossCameraPipeline | null>(null)
  const [toRestart, setToRestart] = useState<CrossCameraPipeline | null>(null)

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetCrossCameraPipelineList(
      {
        offset: getPageOffset(activePage, PAGE_SIZE),
        limit: PAGE_SIZE,
        ordering: 'name'
      },
      { refetchInterval: POLL_INTERVAL }
    )

  // The endpoint already leaves out applications with fewer than two cameras.
  const { data: appData } = useGetCrossCameraAppList({
    limit: MAX_ITEMS_WITHOUT_PAGINATION,
    ordering: 'name'
  })

  // The table is paged, so it cannot tell which applications already have a pipeline.
  const { data: allPipelineData } = useGetCrossCameraPipelineList({
    limit: MAX_ITEMS_WITHOUT_PAGINATION,
    ordering: 'name'
  })
  const usedApplicationIds =
    allPipelineData?.results.map((pipeline) => pipeline.application.id) ?? []

  const { data: models } = useGetCrossCameraModels()

  const pipelines = data?.results ?? []
  const totalPageCount = getPageCount(data?.count ?? 0, PAGE_SIZE)

  useEffect(() => {
    if (totalPageCount > 0 && activePage > totalPageCount) {
      setTableState({ activePage: totalPageCount })
    }
  }, [activePage, totalPageCount, setTableState])

  const handleCreateClick = () => {
    setEditing(null)
    openForm()
  }

  const handleEdit = (pipeline: CrossCameraPipeline) => {
    setEditing(pipeline)
    openForm()
  }

  return (
    <>
      <Paper p="sm" mb="md" bg="gray.0" withBorder>
        <Flex justify="space-between" align="center">
          {canCreate && (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleCreateClick}
            >
              <FormattedMessage id="crossCamera.pipelines.actions.create" />
            </Button>
          )}

          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            loading={isFetching}
            onClick={() => void refetch()}
          >
            <FormattedMessage id="refresh" />
          </Button>
        </Flex>
      </Paper>

      <CrossCameraPipelineList
        pipelines={pipelines}
        isLoading={isLoading}
        hasError={isError}
        errorMessage={
          isError
            ? extractErrorMessage(
                error,
                intl.formatMessage({ id: 'crossCamera.pipelines.error' })
              )
            : undefined
        }
        onReload={() => void refetch()}
        onEdit={handleEdit}
        onDelete={setToDelete}
        onRestart={setToRestart}
      />

      <Flex justify="end" mt="lg">
        <Pagination
          size="sm"
          total={totalPageCount}
          value={activePage}
          onChange={(value) => setTableState({ activePage: value })}
        />
      </Flex>

      <Modal
        opened={formOpen}
        size="lg"
        title={
          <FormattedMessage
            id={
              editing
                ? 'crossCamera.pipelines.actions.edit'
                : 'crossCamera.pipelines.actions.create'
            }
          />
        }
        onClose={closeForm}
      >
        <CrossCameraPipelineFormHandler
          pipeline={editing ?? undefined}
          applications={appData?.results ?? []}
          usedApplicationIds={usedApplicationIds}
          models={models ?? []}
          onClose={closeForm}
        />
      </Modal>

      <CrossCameraPipelineDeleteModal
        pipeline={toDelete}
        onClose={() => setToDelete(null)}
      />

      <CrossCameraPipelineRestartModal
        pipeline={toRestart}
        onClose={() => setToRestart(null)}
      />
    </>
  )
}
